import React, { useCallback } from 'react';
import { ButtonV2, Flex } from '@withjoy/joykit';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useListingsPanelTelemetry } from '../ListingsPanel.telemetry';
import { withWindow } from '@shared/utils/withWindow';
import { useAccommodationsConsoleConfig } from '@apps/accommodations/AccommodationsConsoleConfigContext';
import { AccommodationListingDTO } from '@apps/accommodations/AccommodationsConsole.types';
import { useHotelBookingGuestSiteTelemetry } from '@apps/guest/routes/HotelBooking/HotelBooking.telemetry';
import { convertToRedirectTelemetry } from '@apps/guest/routes/HotelBooking/components/PartnerHotelRedirect/PartnerHotelRedirect.utils';
import { RedirectMechanismType } from '@graphql/generated';
import { createUtmParametersForAccommodationsConsolePage } from '@apps/accommodations/hooks/useAccommodationRedirectUrl';

interface HotelCTAProps {
  listing: AccommodationListingDTO;
  redirectUrl?: string;
}

export const HotelCTA = ({ listing, redirectUrl }: HotelCTAProps) => {
  const telemetry = useListingsPanelTelemetry();
  const config = useAccommodationsConsoleConfig();
  const hotelBookingGuestSiteTelemetry = useHotelBookingGuestSiteTelemetry();

  const handleSeeAvailability = useEventCallback((name?: string) => {
    telemetry.seeAvailabilityClicked({ joyPlaceId: listing.joyPlaceId || '', hotelName: listing.name, name });

    const telemetryPayload = convertToRedirectTelemetry({
      redirectData: listing?.redirectMechanisms?.find(mechanism => mechanism.type === RedirectMechanismType.joyNativeCheckout),
      utmParams: createUtmParametersForAccommodationsConsolePage(),
      hasHotelSearchPageUrl: false,
    });

    hotelBookingGuestSiteTelemetry.guestPartnerHotelBookingRedirect({
      ...telemetryPayload,
      destinationUrl: new URL(withWindow(global => `${global.location.origin}${redirectUrl}`, redirectUrl || '')),
    });
  });

  const handleClick = useEventCallback(e => {
    e.preventDefault();
    e.stopPropagation();
    if (config.listingsPanel?.listingEntry?.bookingCta?.onButtonClick) {
      handleSeeAvailability(
        listing.isPickedByCouple ? config.listingsPanel.listingEntry.bookingCta.telemetryNameForCouplePicks : config.listingsPanel.listingEntry.bookingCta.telemetryName,
      );
      config.listingsPanel.listingEntry.bookingCta.onButtonClick(listing);
    } else {
      handleSeeAvailability();
      withWindow(global => global.open(redirectUrl, '_blank'));
    }
  });

  const getBookingConfig = useCallback(() => {
    return listing?.isPickedByCouple && config.listingsPanel.listingEntry.bookingCta.couplePicks
      ? config.listingsPanel.listingEntry.bookingCta.couplePicks
      : config.listingsPanel.listingEntry.bookingCta.default;
  }, [listing?.isPickedByCouple, config.listingsPanel.listingEntry.bookingCta]);

  const getButtonProps = useCallback(() => {
    const { buttonProps } = getBookingConfig();
    return buttonProps;
  }, [getBookingConfig]);

  return (
    <Flex width={{ _: '100%', md: 'fit-content' }}>
      <ButtonV2
        {...getButtonProps()}
        height={{ _: '48px', md: '40px' }}
        width="100%"
        onClick={handleClick}
        shape="rounded"
      >
        {getBookingConfig().buttonTitle}
      </ButtonV2>
    </Flex>
  );
};
